import { useCallback, useEffect, useState } from "react";
import { Outlet, Link } from "react-router-dom";
import {
  adminMe,
  fetchMe,
  loginAccount,
  logoutAccount,
  setMyCardPrefs,
  setMyName,
  signupAccount,
} from "./lib/api.js";
import type {
  ApiMe,
  CardBackId,
  CardThemeId,
  PlayerIdentity,
} from "../../shared/protocol.js";
import { CardBackProvider, CardStyleProvider } from "./components/cardThemes.js";
import { SettingsSheet } from "./components/SettingsSheet.js";

export function App() {
  const [me, setMe] = useState<PlayerIdentity | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draftName, setDraftName] = useState("");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    fetchMe().then((r: ApiMe) => {
      setMe(r.identity);
      setIsMobile(r.isMobile);
    }).catch((e) => {
      // eslint-disable-next-line no-console
      console.error("failed to fetch identity", e);
    });
  }, []);

  // Probe admin status whenever the identity changes (login/logout/signup
  // can promote-or-demote). 401/404 from the endpoint just means "no" — we
  // don't want a noisy console error for the common non-admin case.
  useEffect(() => {
    if (!me) {
      setIsAdmin(false);
      return;
    }
    adminMe()
      .then((r) => setIsAdmin(r.isAdmin))
      .catch(() => setIsAdmin(false));
  }, [me?.id]);

  // Apply a body class so CSS media queries (e.g. mobile-rotation in game)
  // can target real mobile devices instead of just narrow viewports.
  useEffect(() => {
    if (isMobile) document.body.classList.add("mobile-device");
    else document.body.classList.remove("mobile-device");
    return () => document.body.classList.remove("mobile-device");
  }, [isMobile]);

  async function commitName() {
    if (!draftName.trim()) {
      setEditing(false);
      return;
    }
    const updated = await setMyName(draftName);
    setMe(updated.identity);
    setEditing(false);
  }

  const refreshMe = useCallback(async () => {
    const r = await fetchMe();
    setMe(r.identity);
    setIsMobile(r.isMobile);
  }, []);

  const onSignup = useCallback(async (username: string, password: string) => {
    const r = await signupAccount(username, password);
    setMe(r.identity);
    return r;
  }, []);

  const onLogin = useCallback(async (username: string, password: string) => {
    const r = await loginAccount(username, password);
    setMe(r.identity);
    return r;
  }, []);

  const onLogout = useCallback(async () => {
    const r = await logoutAccount();
    setMe(r.identity);
    return r;
  }, []);

  const onSetStyle = useCallback(async (cardTheme: CardThemeId) => {
    const r = await setMyCardPrefs({ cardTheme });
    setMe(r.identity);
  }, []);

  const onSetBack = useCallback(async (cardBack: CardBackId) => {
    const r = await setMyCardPrefs({ cardBack });
    setMe(r.identity);
  }, []);

  const styleId: CardThemeId = me?.cardTheme ?? "bold";
  const backId: CardBackId = me?.cardBack ?? "blue";

  return (
    <CardStyleProvider styleId={styleId}>
    <CardBackProvider backId={backId}>
      <div className="app-shell">
        <header className="topbar">
          <Link to="/" className="brand">THROW CARDS</Link>
          <div className="topbar-right">
            {me ? (
              <>
                {editing ? (
                  <span className="row" style={{ gap: 6 }}>
                    <input
                      autoFocus
                      value={draftName}
                      onChange={(e) => setDraftName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") commitName();
                        if (e.key === "Escape") setEditing(false);
                      }}
                      style={{ width: 160 }}
                    />
                    <button onClick={commitName}>save</button>
                  </span>
                ) : (
                  <button
                    className="topbar-name-btn"
                    onClick={() => { setDraftName(me.displayName); setEditing(true); }}
                    title="Change display name"
                  >
                    {me.displayName}
                  </button>
                )}
                {isAdmin && (
                  <Link to="/admin" className="topbar-name-btn" title="Moderation tools">
                    Admin
                  </Link>
                )}
                <button
                  className="icon-btn"
                  onClick={() => setSettingsOpen(true)}
                  title="Settings — account, card style &amp; card back"
                  aria-label="Settings"
                >
                  ⚙
                </button>
              </>
            ) : (
              <span>…</span>
            )}
          </div>
        </header>
        <Outlet
          context={{
            me,
            isMobile,
            refreshMe,
          }}
        />
        {settingsOpen && (
          <SettingsSheet
            me={me}
            styleId={styleId}
            backId={backId}
            onClose={() => setSettingsOpen(false)}
            onSetStyle={onSetStyle}
            onSetBack={onSetBack}
            onSignup={onSignup}
            onLogin={onLogin}
            onLogout={onLogout}
          />
        )}
      </div>
    </CardBackProvider>
    </CardStyleProvider>
  );
}

export interface AppContext {
  me: PlayerIdentity | null;
  isMobile: boolean;
  refreshMe: () => Promise<void>;
}
